import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, User, MessageSquare, Send, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { API } from '@/App';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Message sent successfully!');
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4" data-testid="contact-title">
              Get In Touch
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Have a question about our afternoon teas, want to book a catering event, or just want to say hello? We'd love to hear from you!
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-background-paper rounded-2xl p-8 border border-primary/20">
              <h2 className="text-2xl font-heading font-bold text-white mb-6">Send us a Message</h2>
              
              {submitted ? (
                <div className="text-center py-8" data-testid="contact-success">
                  <div className="w-16 h-16 bg-status-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-status-success" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-white mb-2">Message Sent!</h3>
                  <p className="text-text-secondary mb-6">Thank you for reaching out. We'll get back to you as soon as possible.</p>
                  <Button 
                    onClick={() => setSubmitted(false)}
                    className="bg-primary hover:bg-primary-hover text-white rounded-full px-6"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2" htmlFor="name">
                      Your Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Jane Smith"
                        className="pl-10 bg-background-elevated border-primary/20 text-white placeholder:text-text-muted rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2" htmlFor="email">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="jane@example.com"
                        className="pl-10 bg-background-elevated border-primary/20 text-white placeholder:text-text-muted rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2" htmlFor="message">
                      Your Message
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-text-muted" />
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us how we can help..."
                        className="pl-10 bg-background-elevated border-primary/20 text-white placeholder:text-text-muted rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary min-h-[150px]"
                        required
                        data-testid="contact-message-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-3 text-lg shadow-lg shadow-primary/30 hover:shadow-primary/50 button-primary"
                    data-testid="contact-submit-button"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="bg-background-paper rounded-2xl p-8 border border-primary/20">
              <h2 className="text-2xl font-heading font-bold text-white mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-1">Email Us</h3>
                    <p className="text-text-secondary">hello@cakerywonderland.com</p>
                    <p className="text-text-muted text-sm mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-1">Call Us</h3>
                    <p className="text-text-secondary">+44 (0) 123 456 7890</p>
                    <p className="text-text-muted text-sm mt-1">Mon-Fri 9am-5pm GMT</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-1">Visit Us</h3>
                    <p className="text-text-secondary">London, United Kingdom</p>
                    <p className="text-text-muted text-sm mt-1">By appointment only</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-1">Business Hours</h3>
                    <p className="text-text-secondary">Monday - Friday: 9am - 5pm</p>
                    <p className="text-text-secondary">Saturday: 10am - 4pm</p>
                    <p className="text-text-muted text-sm mt-1">Closed Sundays & Bank Holidays</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Teaser */}
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-8 border border-primary/30">
              <h3 className="text-xl font-heading font-bold text-white mb-3">Frequently Asked Questions</h3>
              <p className="text-text-secondary mb-4">
                Looking for quick answers? Check out our most common questions about ordering, delivery, and catering.
              </p>
              <div className="space-y-3 text-sm">
                <div className="bg-background/50 rounded-lg p-3">
                  <p className="text-primary font-medium">Do you deliver nationwide?</p>
                  <p className="text-text-muted mt-1">Yes! We deliver our afternoon tea boxes across the UK.</p>
                </div>
                <div className="bg-background/50 rounded-lg p-3">
                  <p className="text-primary font-medium">How far in advance should I book?</p>
                  <p className="text-text-muted mt-1">We recommend booking at least 7 days in advance for catering events.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
